import { useEffect, useMemo, useState } from 'react'
import { Bookmark, Loader2, Search } from 'lucide-react'
import { showApiError } from '@/lib/utils/notifications'
import { fetchFrequentlyUsedNotes } from '../../services/residentApi'
import { ResidentDialogShell } from './ResidentDialogShell'
import type { FrequentlyUsedNote, FrequentlyUsedNotesDialogProps } from '../../types'





function parseFrequentlyUsedNotesResponse(response: unknown): FrequentlyUsedNote[] {
  if (Array.isArray(response)) return response as FrequentlyUsedNote[]
  if (response && typeof response === 'object' && Array.isArray((response as { data?: unknown }).data)) {
    return (response as { data: FrequentlyUsedNote[] }).data
  }
  return []
}

function noteLabel(note: FrequentlyUsedNote): string {
  return String(note.note_text ?? '').trim()
}

/**
 * Mirror Angular `normalizeHashtagNote` (frequently-used-notes-popup.component.ts:55-61):
 * never adds `#`. Leaves plain notes unchanged; for `#`-notes, strips the leading space
 * (`# foo` -> `#foo`).
 */
function normalizeHashtagNote(noteText: string): string {
  const t = (noteText || '').trim()
  if (!t || t.charAt(0) !== '#') {
    return t
  }
  return '#' + t.substring(1).replace(/^\s+/, '')
}

export function FrequentlyUsedNotesDialog({
  isOpen,
  onClose,
  onInsert,
  subtitle = 'Select one or more notes to add',
  footerNote = 'Selected notes will be appended to the note field.',
}: FrequentlyUsedNotesDialogProps) {
  const [notes, setNotes] = useState<FrequentlyUsedNote[]>([])
  const [selected, setSelected] = useState<string[]>([])
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')

  useEffect(() => {
    if (!isOpen) return
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: reset selection + show loading, then fetch notes when the dialog opens
    setSelected([])
    setSearch('')
    setLoading(true)
    void fetchFrequentlyUsedNotes()
      .then((response) => setNotes(parseFrequentlyUsedNotesResponse(response)))
      .catch(showApiError)
      .finally(() => setLoading(false))
  }, [isOpen])

  const visibleNotes = useMemo(() => {
    const query = search.trim().toLowerCase()
    if (!query) return notes
    return notes.filter((note) => noteLabel(note).toLowerCase().includes(query))
  }, [notes, search])

  const insert = () => {
    if (!selected.length) return
    const joined = selected
      .map((line) => normalizeHashtagNote(line))
      .filter((line) => line.length > 0)
      .join('\n')
    onInsert(joined)
    onClose()
  }

  return (
    <ResidentDialogShell
      isOpen={isOpen}
      title="Frequently Used Notes"
      headerVariant="iconTitle"
      titleIcon={<Bookmark className="h-4 w-4" />}
      subtitle={subtitle}
      maxWidth="max-w-lg"
      onClose={onClose}
      panelDataTestId="frequently-used-notes-dialog-panel"
      footer={
        <div
          className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
          data-testid="frequently-used-notes-dialog-footer"
        >
          <p className="m-0 text-xs text-slate-500">{footerNote}</p>
          <div className="flex shrink-0 items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              data-testid="frequently-used-notes-dialog-cancel-button"
              className="rounded-lg border border-[#1597ff] px-5 py-2 text-sm font-semibold text-[#1597ff] transition hover:bg-[#eaf4ff] disabled:opacity-60"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={insert}
              disabled={loading || !selected.length}
              data-testid="frequently-used-notes-dialog-insert-button"
              className="rounded-lg bg-[#1597ff] px-5 py-2 text-sm font-semibold text-white transition hover:bg-[#0d7acc] shadow-2xs disabled:opacity-50"
            >
              Insert Notes
            </button>
          </div>
        </div>
      }
    >
      <div className="flex min-h-0 flex-col" data-testid="frequently-used-notes-dialog-body">
        <div className="relative mb-3">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
            aria-hidden="true"
          />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search notes"
            disabled={loading}
            className="w-full rounded-lg border border-slate-200 bg-white py-2.5 pl-9 pr-3 text-sm text-slate-700 outline-none transition focus:border-[#1597ff] focus:ring-2 focus:ring-[#1597ff]/20 disabled:bg-slate-50"
            data-testid="frequently-used-notes-dialog-search"
          />
        </div>

        {loading ? (
          <p
            className="flex items-center gap-2 py-6 text-sm text-slate-600"
            data-testid="frequently-used-notes-dialog-loading"
          >
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            Loading notes...
          </p>
        ) : null}
        {!loading ? (
          <div className="space-y-2">
            <div className="flex items-center justify-between px-1 pb-1">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Select notes
              </span>
              <div className="flex items-center gap-3">
                <span className="text-xs font-medium text-slate-500">{selected.length} selected</span>
                <button
                  type="button"
                  onClick={() => setSelected([])}
                  disabled={!selected.length}
                  data-testid="frequently-used-notes-dialog-clear-all"
                  className="text-xs font-semibold text-slate-500 hover:text-slate-700 disabled:text-slate-300"
                >
                  Clear all
                </button>
              </div>
            </div>

            <div className="max-h-[min(60vh,28rem)] overflow-y-auto rounded-lg border border-slate-200">
              {!visibleNotes.length ? (
                <p
                  className="py-4 text-center text-sm font-medium text-slate-500"
                  data-testid="frequently-used-notes-dialog-empty"
                >
                  {notes.length ? 'No notes match your search.' : 'No frequently used notes available.'}
                </p>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {visibleNotes.map((note, index) => {
                    const text = noteLabel(note)
                    if (!text) return null
                    const noteKey = String(note.id ?? index)
                    const checked = selected.includes(text)
                    return (
                      <li key={noteKey} data-testid={`frequently-used-notes-dialog-row-${noteKey}`}>
                        <label
                          className={`flex cursor-pointer items-start gap-3 p-3 text-sm font-medium transition-colors ${
                            checked ? 'bg-sky-50/70 text-slate-900' : 'bg-white text-slate-700 hover:bg-slate-50'
                          }`}
                        >
                          <input
                            type="checkbox"
                            className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300 text-[#1597ff] focus:ring-blue-100 cursor-pointer"
                            checked={checked}
                            onChange={(e) =>
                              setSelected(
                                e.target.checked
                                  ? [...selected, text]
                                  : selected.filter((item) => item !== text),
                              )
                            }
                            data-testid={`frequently-used-notes-dialog-row-${noteKey}-checkbox`}
                          />
                          <span className="min-w-0 flex-1 leading-relaxed break-words">{text}</span>
                        </label>
                      </li>
                    )
                  })}
                </ul>
              )}
            </div>
          </div>
        ) : null}
      </div>
    </ResidentDialogShell>
  )
}
